/**
 * Global fetch() interceptor — same-origin safety net.
 *
 * Why this exists: a deployed React bundle can have REACT_APP_BACKEND_URL
 * baked in as the internal Emergent hostname (e.g. native-commerce-1.emergent.host).
 * When the site is also served from a custom domain (entreprenatives.com) the
 * baked-in URL causes cross-origin requests that fail for some users —
 * corporate firewalls, ad-blockers, strict DNS resolvers, etc. — with the
 * generic "Failed to fetch" / "Could not connect to the internet" error.
 *
 * Solution: at app boot, wrap BOTH window.fetch AND axios. If we're served
 * from a custom domain AND the outgoing URL points to an Emergent internal
 * domain, rewrite the outgoing URL to the current origin. Net effect: all
 * /api/* calls go same-origin when running on the real public domain,
 * without changing any call sites.
 *
 * On the preview (native-commerce-1.preview.emergentagent.com) this is a no-op
 * because the current origin IS an internal domain.
 */

(function patchFetch() {
  if (typeof window === 'undefined' || !window.fetch) return;

  const INTERNAL_HOSTS = [
    'emergent.host',
    'emergentagent.com',
    'preview.emergentagent.com',
  ];

  const isInternalHost = (hostname) =>
    INTERNAL_HOSTS.some((d) => hostname === d || hostname.endsWith('.' + d) || hostname.endsWith(d));

  const currentHost = window.location.hostname;
  const currentOrigin = window.location.origin;
  const servedFromCustomDomain = !isInternalHost(currentHost);

  if (!servedFromCustomDomain) return; // nothing to rewrite on preview

  const originalFetch = window.fetch.bind(window);

  window.fetch = function patchedFetch(input, init) {
    try {
      const toUrlString = (x) => (typeof x === 'string' ? x : (x && x.url) || '');
      const urlStr = toUrlString(input);
      if (urlStr && /^https?:\/\//i.test(urlStr)) {
        const u = new URL(urlStr);
        if (isInternalHost(u.hostname)) {
          // Rewrite to current origin, keep path + query + hash
          const rewritten = currentOrigin + u.pathname + u.search + u.hash;
          if (typeof input === 'string') {
            input = rewritten;
          } else {
            input = new Request(rewritten, input);
          }
        }
      }
    } catch (_) {
      // swallow — fall through to original fetch on any parse error
    }
    return originalFetch(input, init);
  };

  try {
    // eslint-disable-next-line no-console
    console.info(`[EN] fetch() same-origin patch active (serving from ${currentOrigin})`);
  } catch (_) { /* no-op */ }

  // ── Axios: install request interceptor the moment axios is loaded ──
  // We dynamically import so this patch module stays dependency-light.
  import('axios').then((axiosModule) => {
    const axios = axiosModule.default || axiosModule;
    axios.interceptors.request.use((config) => {
      try {
        if (config.url && /^https?:\/\//i.test(config.url)) {
          const u = new URL(config.url);
          if (isInternalHost(u.hostname)) {
            config.url = currentOrigin + u.pathname + u.search + u.hash;
          }
        } else if (config.baseURL && /^https?:\/\//i.test(config.baseURL)) {
          const u = new URL(config.baseURL);
          if (isInternalHost(u.hostname)) {
            config.baseURL = currentOrigin + u.pathname + u.search + u.hash;
          }
        }
      } catch (_) { /* no-op */ }
      return config;
    });
  }).catch(() => { /* axios not present — ignore */ });
})();
